/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getRealLocalStorage } from '@/shared/utils/storage/localStorageWrapper';

/**
 * Checks if authentication state has changed.
 * 
 * @param {boolean} authIsAuthenticated - Auth context authenticated state
 * @param {boolean} isAuthenticated - Local authenticated state
 * @returns {boolean} True if state changed
 */
export function hasAuthStateChanged(authIsAuthenticated: boolean, isAuthenticated: boolean): boolean {
  return authIsAuthenticated !== isAuthenticated;
}

/**
 * Checks if user exists but hasn't been checked.
 * 
 * @param {any} user - User object
 * @param {boolean} hasCheckedAuth - Whether auth has been checked
 * @returns {boolean} True if user exists and not checked
 */
export function hasUserButNotChecked(user: any, hasCheckedAuth: boolean): boolean {
  return !!(user && !hasCheckedAuth);
}

/**
 * Determines if authentication check should be performed.
 * 
 * @param {boolean} hasCheckedAuth - Whether auth has been checked
 * @param {boolean} authStateChanged - Whether auth state changed
 * @param {boolean} userButNotChecked - Whether user exists but not checked
 * @returns {boolean} True if check should be performed
 */
export function shouldCheckAuthentication(
  hasCheckedAuth: boolean,
  authStateChanged: boolean,
  userButNotChecked: boolean
): boolean {
  if (!hasCheckedAuth) {
    return true;
  }
  if (authStateChanged) {
    return true;
  }
  if (userButNotChecked) {
    return true;
  }
  return false;
}

/**
 * Handles successful authentication check.
 * When auth fails on a protected /app route, saves the current path
 * and triggers a full re-authentication redirect to prevent blank pages.
 * 
 * @param {boolean} result - Authentication result
 * @param {Function} setIsAuthenticated - Setter for authenticated state
 * @param {object} ref - Ref object to update
 * @returns {void}
 */
export function handleAuthCheckSuccess(
  result: boolean,
  setIsAuthenticated: (value: boolean) => void,
  ref: { current: boolean }
): void {
  setIsAuthenticated(result);
  ref.current = true;
  if (result) return;
  if (!window.location.pathname.startsWith('/app')) return;
  try { getRealLocalStorage().setItem('returnPath', window.location.href); } catch { /* */ }
  if (typeof window.location.replace === 'function') window.location.replace('/');
}

/**
 * Handles failed authentication check.
 * On protected /app routes, saves return path and redirects to landing page
 * so the user can re-authenticate instead of seeing a blank page.
 * 
 * @param {Error} error - Error object
 * @param {Function} setGlobalError - Error setter
 * @param {Function} setIsAuthenticated - Setter for authenticated state
 * @param {object} ref - Ref object to update
 * @returns {void}
 */
export function handleAuthCheckError(
  error: Error,
  setGlobalError: (error: any) => void,
  setIsAuthenticated: (value: boolean) => void,
  ref: { current: boolean }
): void {
  setGlobalError(error);
  setIsAuthenticated(false);
  ref.current = true;

  if (window.location.pathname.startsWith('/app')) {
    try { getRealLocalStorage().setItem('returnPath', window.location.href); } catch { /* */ }
    if (typeof window.location.replace === 'function') {
      window.location.replace('/');
    }
  }
}

/**
 * Performs authentication check.
 * 
 * @param {any} auth - Auth context
 * @param {Function} determineAuthStatusAsync - Async auth status function
 * @param {Function} setIsCheckingAuth - Setter for checking state
 * @param {Function} addLoading - Loading add function
 * @param {Function} setIsAuthenticated - Setter for authenticated state
 * @param {Function} setGlobalError - Error setter
 * @param {Function} removeLoading - Loading remove function
 * @param {object} ref - Ref object
 * @returns {void}
 */
export function performAuthCheck(
  auth: any,
  determineAuthStatusAsync: (auth: any) => Promise<boolean>,
  setIsCheckingAuth: (value: boolean) => void,
  addLoading: () => void,
  setIsAuthenticated: (value: boolean) => void,
  setGlobalError: (error: any) => void,
  removeLoading: () => void,
  ref: { current: boolean }
): void {
  setIsCheckingAuth(true);

  determineAuthStatusAsync(auth)
    .then((result) => handleAuthCheckSuccess(result, setIsAuthenticated, ref))
    .catch((error) => handleAuthCheckError(error, setGlobalError, setIsAuthenticated, ref))
    .finally(() => {
      setIsCheckingAuth(false);
    });
}
